import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

//CSS
import "./companySettingsPage.css";
import "../0PageStyles.css";

//Components
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import PageLoader from "../../Components/Other Components/pageLoader";
import PageWrapper from "../pageWrapper";
import Title from "../../Components/Other Components/Title";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";

//Functions and hooks
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import useFormState from "../../hooks/useFormStates";
import { useSettings } from "../../Contexts/SettingsContext";
import { UpdateCompanySettings } from "../../BackendAccess/SettingsHooks";
import {
  EditCompanyData,
  GetCompanyData,
  DeleteCompany,
} from "../../BackendAccess/CompanyHooks";
import { usePopup } from "../../Contexts/PopupContext";
import { useTranslation } from "react-i18next";

export default function CompanySettingsPage() {
  const [editing, setEditing] = useState(false);

  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Replace separate states with a single object
  const [companySettingsData, setCompanySettingsData] = useState({
    currency: "USD",
    language: "en",
  });
  const [companySettingsSet, setCompanySettingsSet] = useState(false);

  const { refreshCompanySettings, companySettings } = useSettings();

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const isNarrow = useNarrowMode();

  const { formData, setField, loadInitialData, hasChanged } = useFormState({});

  const navigate = useNavigate();
  const { showPopup, removePopup } = usePopup();
  const { t } = useTranslation();

  // Initialize company settings from context
  useEffect(() => {
    if (companySettings && !companySettingsSet) {
      setCompanySettingsData({
        currency: companySettings.currency || "USD",
        language: companySettings.language || "en",
      });
      setCompanySettingsSet(true);
    }
  }, [companySettings, companySettingsSet]);

  const showDeletePopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style="warning"
        message={
          <p>
            {t("Are you sure you want to delete your company?", {
              ns: "settings",
            })}
            <br />
            {t(
              "This will result in your company account and all the data being deleted!",
              { ns: "settings" }
            )}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => deleteCompany(key)}
        onCancel={() => removePopup(key)}
        highSecurity={true}
      />,
      key
    );
  };

  const deleteCompany = async (key) => {
    showNotification(t("Deleting company...", { ns: "settings" }), "info");
    try {
      const [data, error] = await DeleteCompany(formData.CompanyID);
      if (error) {
        setError(error);
      } else {
        showNotification(t("Company deleted.", { ns: "settings" }), "success");
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      removePopup(key);
    }
  };

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetCompanyData();
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        loadInitialData(data);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const OnConfirmChanges = async () => {
    setEditing(true);
    showNotification(t("Editing company data...", { ns: "settings" }), "info");
    try {
      const [data, error] = await EditCompanyData(formData);
      if (error) {
        setError(error);
      } else {
        loadInitialData(formData);
        showNotification(
          t("Company data edited successfully!", { ns: "settings" }),
          "success"
        );
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setEditing(false);
    }
  };

  const clickOnCancel = () => {
    navigate(-1);
  };

  const clickOnDelete = () => {
    showDeletePopup();
  };

  const handleInputFieldChange = (field, value) => {
    setField(field, value);
  };

  // Updated function to handle company settings changes
  const handleCompanySettingsChange = (field, value) => {
    const updatedSettings = {
      ...companySettingsData,
      [field]: value,
    };

    setCompanySettingsData(updatedSettings);

    UpdateCompanySettings({
      ...updatedSettings,
    });

    refreshCompanySettings();
  };

  const companySettingsArray = useMemo(() => {
    return [
      {
        CategoryName: t("Company Localization", { ns: "settings" }),
        CategorySettings: [
          {
            Name: t("Currency", { ns: "settings" }),
            Type: "dropdown",
            Options: [
              { label: "US Dollar (USD)", value: "USD" },
              { label: "Euro (EUR)", value: "EUR" },
              { label: "Serbian Dinar (RSD)", value: "RSD" },
              { label: "British Pound (GBP)", value: "GBP" },
            ],
            Value: companySettingsData.currency,
            OnChange: (val) => {
              handleCompanySettingsChange("currency", val);
            },
            TutorialId: "settings-currency",
          },
          {
            Name: t("Company Language", { ns: "settings" }),
            Type: "dropdown",
            Options: [
              { label: "English", value: "en" },
              { label: "Srpski", value: "rs" },
            ],
            Value: companySettingsData.language,
            OnChange: (val) => {
              handleCompanySettingsChange("language", val);
            },
            TutorialId: "settings-company-language",
          },
        ],
      },
    ];
  }, [companySettingsData, companySettingsSet]);

  const createCompanySettings = () => {
    let ZCounter = companySettingsArray.length + 1;
    return companySettingsArray.map((settingCategory) => {
      ZCounter--;
      return (
        <div
          className="general-settings-category"
          style={{ zIndex: ZCounter }}
          key={settingCategory.CategoryName}
        >
          <span className="general-settings-category-title">
            {settingCategory.CategoryName}:
          </span>
          <InputGroup className="general-settings-category-list">
            {settingCategory.CategorySettings.map((setting) => {
              return (
                <InputField
                  key={setting.Name}
                  label={setting.Name + ":"}
                  onChange={setting.OnChange}
                  type={setting.Type ?? ""}
                  placeholder={setting.Placeholder ?? ""}
                  options={setting.Options ?? []}
                  value={setting.Value}
                  min={setting.Min ?? null}
                  max={setting.Max ?? null}
                  step={setting.Step ?? null}
                  copyEnabled={false}
                  dropdownSortType={setting.SortType}
                  tutorialId={setting.TutorialId}
                />
              );
            })}
          </InputGroup>
        </div>
      );
    });
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Company settings", { ns: "tutorial" }),
      message: t(
        "This page controls your company's shared details, localization, and account-level actions - these affect everyone in your company, not just you.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "company-settings-basic",
      title: t("Company details", { ns: "tutorial" }),
      message: t(
        "Your company's ID (fixed), name, registration number, email, phone, and website.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "company-settings-location",
      title: t("Location & owner", { ns: "tutorial" }),
      message: t(
        "Your company's country, city, address, and postal code. The owner is shown for reference and can't be changed here.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "company-settings-apply-btn",
      title: t("Save your changes", { ns: "tutorial" }),
      message: t(
        "Once you're happy with your changes, click here to save them. This button only lights up once something has actually changed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "company-settings-delete-btn",
      title: t("Delete Company", { ns: "tutorial" }),
      message: t(
        "Permanently deletes your company account and all its data. This cannot be undone, so use it carefully.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "settings-currency",
      title: t("Currency", { ns: "tutorial" }),
      message: t(
        "The currency used for prices across the whole app - item prices, traffic notes, and reports.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "settings-company-language",
      title: t("Company Language", { ns: "tutorial" }),
      message: t(
        "The default language new employees will see, and the language used in shared documents like printed notes.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="companySettings" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">Company settings</Title>
          <div className="container-bordered company-settings w-90">
            <div data-tutorial-id="company-settings-basic">
            <InputGroup className="container-empty vertical mr-3">
              <InputField
                label={t("Company ID") + ":"}
                readOnly={true}
                value={formData.CompanyID}
              />
              <InputField
                label={t("Name") + ":"}
                mandatory={true}
                value={formData.Name}
                onChange={(e) => handleInputFieldChange("Name", e)}
              />

              <InputField
                label={t("Registration Num") + ":"}
                value={formData.RegistrationNumber}
                onChange={(e) =>
                  handleInputFieldChange("RegistrationNumber", e)
                }
              />

              <InputField
                label={t("Email") + ":"}
                value={formData.Email}
                onChange={(e) => handleInputFieldChange("Email", e)}
              />

              <InputField
                label={t("Phone") + ":"}
                value={formData.Phone}
                onChange={(e) => handleInputFieldChange("Phone", e)}
              />
              <InputField
                label={t("Website") + ":"}
                value={formData.Website}
                onChange={(e) => handleInputFieldChange("Website", e)}
              />
            </InputGroup>
            </div>
            <div data-tutorial-id="company-settings-location">
            <InputGroup className="container-empty vertical mr-3 ">
              <InputField
                label={t("Country") + ":"}
                value={formData.Country}
                onChange={(e) => handleInputFieldChange("Country", e)}
              />
              <InputField
                label={t("City") + ":"}
                value={formData.City}
                onChange={(e) => handleInputFieldChange("City", e)}
              />

              <InputField
                label={t("Address") + ":"}
                value={formData.Address}
                onChange={(e) => handleInputFieldChange("Address", e)}
              />

              <InputField
                label={t("PostalCode") + ":"}
                value={formData.PostalCode}
                onChange={(e) => handleInputFieldChange("PostalCode", e)}
              />

              <InputField
                label={t("Owner") + ":"}
                readOnly={true}
                value={formData.OwnerName}
              />
            </InputGroup>
            </div>
          </div>

          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle={
                hasChanged && !editing ? "btn-positive" : "btn-disabled"
              }
              onClick={OnConfirmChanges}
              acceptEnter={true}
              icon={"apply"}
              tutorialId="company-settings-apply-btn"
            >
              {t("Apply Changes")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-negative"
              onClick={clickOnDelete}
              icon={"delete"}
              disabled={editing}
              tutorialId="company-settings-delete-btn"
            >
              {t("Delete Company")}
            </Button>
          </div>
          {createCompanySettings()}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
